import { useParams, Navigate } from "react-router-dom";
import { Clock, Tag } from "lucide-react";
import { getGuide, GUIDES } from "@/data/guides";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { SectionRenderer } from "@/components/blocks/SectionRenderer";
import { RelatedTickets } from "@/components/cards/RelatedTickets";
import { RelatedLinks } from "@/components/cards/RelatedLinks";
import { CTASection } from "@/components/common/CTASection";

export default function GuideDetail() {
  const { slug } = useParams();
  const page = getGuide(slug);
  if (!page) return <Navigate to="/guides" replace />;

  const relatedGuideLinks = (page.relatedGuides || [])
    .map((s) => GUIDES.find((g) => g.slug === s))
    .filter(Boolean)
    .map((g) => ({ label: g.title, to: `/guides/${g.slug}` }));

  const meta = (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85">
      <span className="flex items-center gap-1.5">
        <Tag size={14} className="text-gold-light" /> {page.category}
      </span>
      <span className="flex items-center gap-1.5">
        <Clock size={14} className="text-gold-light" /> {page.readTime}
      </span>
    </div>
  );

  return (
    <>
      <PageHero
        overline="Visitor Guide"
        title={page.title}
        subtitle={page.subtitle}
        image={page.heroImage}
        meta={meta}
        breadcrumbs={[{ label: "Visitor Guides", to: "/guides" }, { label: page.title }]}
      />

      <article className="container-tight py-14 md:py-20">
        <Reveal>
          <p className="font-serif text-2xl md:text-3xl font-light leading-relaxed text-charcoal text-balance">
            {page.intro}
          </p>
        </Reveal>
        <div className="mt-14 md:mt-16">
          <SectionRenderer blocks={page.blocks} />
        </div>

        {(relatedGuideLinks.length > 0 || page.relatedPlanning?.length) && (
          <div className="mt-16 grid gap-8 sm:grid-cols-2">
            {relatedGuideLinks.length > 0 && (
              <RelatedLinks title="More guides" links={relatedGuideLinks} testId="guide-related-guides" />
            )}
            {page.relatedPlanning?.length > 0 && (
              <RelatedLinks title="Plan your visit" links={page.relatedPlanning} testId="guide-related-planning" />
            )}
          </div>
        )}
      </article>

      <RelatedTickets
        slugs={page.relatedTickets}
        overline="Turn reading into doing"
        title="Recommended tickets"
        description="Put this guide into action with the tickets best suited to your trip."
      />

      <div className="container-wide pb-20">
        <CTASection image={page.heroImage} />
      </div>
    </>
  );
}
